import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MarkupAppliedTo } from '@/lib/api';

interface MarkupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  markupType: 'dollars' | 'percent';
  markupValue: number;
  markupAppliedTo: MarkupAppliedTo;
  applicableSubtotals: {
    all: number;
    labor: number;
    product_parts: number;
    other: number;
  };
  onSave: (type: 'dollars' | 'percent', value: number, appliedTo: MarkupAppliedTo) => void;
  currencySymbol?: string;
  formatAmount: (amount: number) => string;
}

export function MarkupModal({
  open,
  onOpenChange,
  markupType: initialType,
  markupValue: initialValue,
  markupAppliedTo: initialAppliedTo,
  applicableSubtotals,
  onSave,
  currencySymbol = '$',
  formatAmount,
}: MarkupModalProps) {
  const [appliedTo, setAppliedTo] = useState<MarkupAppliedTo>(initialAppliedTo);
  const [type, setType] = useState<'dollars' | 'percent'>(initialType);
  const [value, setValue] = useState(initialValue);

  // Reset to initial values when modal opens
  useEffect(() => {
    if (open) {
      setAppliedTo(initialAppliedTo);
      setType(initialType);
      setValue(initialValue);
    }
  }, [open, initialAppliedTo, initialType, initialValue]);

  const getApplicableSubtotal = () => {
    if (appliedTo === 'none') return 0;
    return applicableSubtotals[appliedTo] || 0;
  };

  const calculatePreview = () => {
    if (appliedTo === 'none' || value <= 0) return 0;
    const subtotal = getApplicableSubtotal();
    if (subtotal <= 0) return 0;
    if (type === 'dollars') return value;
    return subtotal * (value / 100);
  };

  const handleApply = () => {
    onSave(type, value, appliedTo);
    onOpenChange(false);
  };

  const handleRemove = () => {
    onSave('percent', 0, 'none');
    onOpenChange(false);
  };

  const preview = calculatePreview();
  const applicableSubtotal = getApplicableSubtotal();

  const getTargetLabel = (target: MarkupAppliedTo) => {
    switch (target) {
      case 'all': return 'all items';
      case 'labor': return 'labor';
      case 'product_parts': return 'parts';
      case 'other': return 'other';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Markup</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-3">
            <Label>Apply markup to</Label>
            <RadioGroup value={appliedTo} onValueChange={(v) => setAppliedTo(v as MarkupAppliedTo)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="markup-none" />
                <Label htmlFor="markup-none" className="font-normal cursor-pointer">
                  None (no markup)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="markup-all" />
                <Label htmlFor="markup-all" className="font-normal cursor-pointer">
                  All Items
                  {applicableSubtotals.all > 0 && (
                    <span className="text-muted-foreground ml-2">({formatAmount(applicableSubtotals.all)})</span>
                  )}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="labor" id="markup-labor" />
                <Label htmlFor="markup-labor" className="font-normal cursor-pointer">
                  Labor Only
                  {applicableSubtotals.labor > 0 && (
                    <span className="text-muted-foreground ml-2">({formatAmount(applicableSubtotals.labor)})</span>
                  )}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="product_parts" id="markup-parts" />
                <Label htmlFor="markup-parts" className="font-normal cursor-pointer">
                  Parts Only
                  {applicableSubtotals.product_parts > 0 && (
                    <span className="text-muted-foreground ml-2">({formatAmount(applicableSubtotals.product_parts)})</span>
                  )}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="other" id="markup-other" />
                <Label htmlFor="markup-other" className="font-normal cursor-pointer">
                  Other Only
                  {applicableSubtotals.other > 0 && (
                    <span className="text-muted-foreground ml-2">({formatAmount(applicableSubtotals.other)})</span>
                  )}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {appliedTo !== 'none' && (
            <div className="space-y-3">
              <Label>Markup amount</Label>
              <div className="flex items-center gap-2">
                <div className="flex rounded-md border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setType('percent')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      type === 'percent'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('dollars')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      type === 'dollars'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {currencySymbol}
                  </button>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={value || ''}
                  onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-32 text-right"
                />
              </div>

              {preview > 0 && applicableSubtotal > 0 && (
                <p className="text-sm text-muted-foreground">
                  Preview: <span className="font-medium text-foreground">+{formatAmount(preview)}</span> on {formatAmount(applicableSubtotal)} of {getTargetLabel(appliedTo)}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            {(initialAppliedTo !== 'none' || initialValue > 0) && (
              <Button type="button" variant="outline" onClick={handleRemove} className="text-destructive hover:text-destructive">
                Remove
              </Button>
            )}
            <div className="flex-1" />
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
